const formatterCache = new Map<string, Intl.NumberFormat>();

function getFormatter(
    locale: string,
    currency: string,
    options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): Intl.NumberFormat {
    const key = `${locale}:${currency}:${options?.minimumFractionDigits ?? ''}:${options?.maximumFractionDigits ?? ''}`;
    if (!formatterCache.has(key)) {
        formatterCache.set(
            key,
            new Intl.NumberFormat(locale, {
                style: 'currency',
                currency,
                currencyDisplay: 'narrowSymbol',
                ...options,
            })
        );
    }
    return formatterCache.get(key) as Intl.NumberFormat;
}

/**
 * Format a number as a currency string
 * @param price - The price to format
 * @param locale - The locale to use for formatting
 * @param currency - The currency code to use
 * @returns Formatted currency string
 */
export function formatCurrency(
    price: number,
    locale: string,
    currency: string,
    options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
): string {
    return getFormatter(locale, currency, options).format(price);
}

/**
 * Extract the currency symbol for a given locale and currency code.
 * Uses the same narrowSymbol display as formatCurrency for consistency.
 * @param locale - The locale to use (e.g. "en-US", "de-DE")
 * @param currency - The ISO 4217 currency code (e.g. "USD", "EUR", "GBP")
 * @returns The narrow currency symbol (e.g. "$", "€", "£", "¥")
 */
export function getCurrencySymbol(locale: string, currency: string): string {
    return (
        getFormatter(locale, currency)
            .formatToParts(0)
            .find((part) => part.type === 'currency')?.value ?? currency
    );
}
